<template>
  <div class="admin-page">
    <AdminStats />

    <results-bar
      v-if="filteredRecipes.length > 0"
      :total="filteredRecipes.length"
      :original-total="allRecipes.length"
      :label="filteredRecipes.length === 1 ? 'opskrift' : 'opskrifter'"
      icon="book"
      tile-selector=".admin-recipe-card"
    />

    <div class="content-page container">
      <div class="admin-controls">
        <button class="lg-btn" @click="createNew">
          <fa icon="plus" /> Create New Recipe
        </button>
        
        <div class="controls-right">
          <div class="view-toggle">
            <button 
              @click="viewMode = 'card'" 
              :class="{ active: viewMode === 'card' }"
              class="view-btn"
              title="Card View"
            >
              <fa icon="th" />
            </button>
            <button 
              @click="viewMode = 'list'" 
              :class="{ active: viewMode === 'list' }"
              class="view-btn"
              title="List View"
            >
              <fa icon="list" />
            </button>
          </div>

          <select v-model="sortBy" class="sort-select">
            <option value="created-desc">Newest First</option>
            <option value="created-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>

          <div class="input-group search-field-container">
            <fa icon="search" />
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search recipes..."
            />
          </div>
        </div>
      </div>

      <div class="recipe-grid" :class="viewMode + '-view'">
        <div
          v-for="recipe in paginatedRecipes"
          :key="recipe.Id"
          :class="['recipe-card', 'admin-recipe-card', viewMode + '-card']"
          @click="editRecipe(recipe)"
        >
          <div
            class="image"
            :style="{ backgroundImage: recipe.ImageUrl ? `url(${getImageUrl(recipe.ImageUrl)})` : 'linear-gradient(var(--main), var(--darker))' }"
          >
            <div class="overlay">
              <fa icon="edit" class="edit-icon" />
            </div>
          </div>
          <div class="card-content">
            <div class="card-info">
              <h6>{{ recipe.Title }}</h6>
              <div class="recipe-meta">
                <span class="date-badge" v-if="recipe.Created">{{ formatDate(recipe.Created) }}</span>
                <span class="done-badge" v-if="recipe.Done">
                  <fa icon="check" />
                </span>
                <span class="time-badge" v-if="recipe.CookingTime">
                  <fa icon="clock" />
                </span>
                <span class="description-badge" v-if="recipe.Description">
                  <fa icon="info" />
                </span>
                <span class="source-badge" v-if="recipe.SourceLink">
                  <fa icon="link" />
                </span>
              </div>
            </div>
            <div class="card-actions">
              <button class="edit-btn" @click.stop="editRecipe(recipe)" title="Edit">
                <fa icon="edit" />
              </button>
              <button class="delete-btn" @click.stop="deleteRecipe(recipe)" title="Delete">
                <fa icon="trash" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Auto-load sentinel: when scrolled into view, the next batch loads. -->
      <div v-show="hasMore" class="admin-auto-load" ref="autoLoadSentinel">
        <div class="auto-load-spinner" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
        <span class="auto-load-text">Indlæser flere opskrifter…</span>
      </div>
    </div>

    <!-- Modal Overlay -->
    <div v-if="showEditor" class="modal-overlay">
      <div class="modal-container" :class="{ 'fullscreen-mode': isFullscreen }" @click.stop>
        <div class="modal-header">
          <button class="modal-action-btn" @click="$refs.recipeEditor.save()" title="Save" :disabled="!hasEditorChanges || !canSaveRecipe">
            <fa icon="save" />
          </button>
          <button class="modal-action-btn fullscreen-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'">
            <fa :icon="isFullscreen ? 'compress' : 'expand'" />
          </button>
          <button class="modal-close" @click="handleCloseEditor">
            <fa icon="times" />
          </button>
        </div>
        <RecipeEditor ref="recipeEditor" :recipeToEdit="selectedRecipe" :isFullscreen="isFullscreen" @saved="handleSaved" @cancel="closeEditor" @toggle-fullscreen="toggleFullscreen" @change-status="handleEditorChangeStatus" @can-save="handleCanSaveStatus" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RecipeEditor from '@/components/admin/RecipeEditor';
import AdminStats from '@/components/admin/AdminStats';
import ResultsBar from '@/components/common/ResultsBar';
import imageHelper from '@/helpers/imageHelper';

export default {
    name: 'Admin',
    components: { RecipeEditor, AdminStats, 'results-bar': ResultsBar },
    data() {
        return {
            showEditor: false,
            selectedRecipe: null,
            debouncedSearch: '',
            searchTimeout: null,
            recipesToShow: 20,
            isFullscreen: false,
            viewMode: 'card',
            sortBy: 'created-desc',
            hasEditorChanges: false,
            canSaveRecipe: false,
            autoLoadObserver: null,
            autoLoading: false
        }
    },
    watch: {
        adminSearchQuery: {
            immediate: true,
            handler(newVal) {
                clearTimeout(this.searchTimeout);
                this.searchTimeout = setTimeout(() => {
                    this.debouncedSearch = newVal;
                    this.recipesToShow = 20; // Reset pagination on search
                }, 300);
            }
        }
    },
    mounted() {
        // Auto-load: when the sentinel enters view (with a 600px head-start)
        // bump recipesToShow. ResultsBar handles its own scroll/resize.
        if (typeof IntersectionObserver !== 'undefined') {
            this.autoLoadObserver = new IntersectionObserver(entries => {
                for (const entry of entries) {
                    if (entry.isIntersecting) this.loadMore();
                }
            }, { rootMargin: '0px 0px 600px 0px', threshold: 0 });
            this.$nextTick(() => {
                if (this.$refs.autoLoadSentinel) {
                    this.autoLoadObserver.observe(this.$refs.autoLoadSentinel);
                }
            });
        }
    },
    beforeDestroy() {
        if (this.autoLoadObserver) this.autoLoadObserver.disconnect();
    },
    computed: {
        ...mapGetters(['allRecipes', 'hasPrivilege', 'adminSearchQuery']),
        searchQuery: {
            get() {
                return this.adminSearchQuery;
            },
            set(value) {
                this.$store.commit('setAdminSearchQuery', value);
            }
        },
        filteredRecipes() {
            // This filters the LOCAL allRecipes array, NOT a database query
            let filtered = this.allRecipes;
            
            if (this.debouncedSearch) {
                const query = this.debouncedSearch.toLowerCase();
                filtered = filtered.filter(recipe => 
                    recipe.Title.toLowerCase().includes(query) ||
                    recipe.Url.toLowerCase().includes(query)
                );
            }

            // Apply sorting
            const sorted = [...filtered];
            switch(this.sortBy) {
                case 'created-desc':
                    sorted.sort((a, b) => new Date(b.Created || 0) - new Date(a.Created || 0));
                    break;
                case 'created-asc':
                    sorted.sort((a, b) => new Date(a.Created || 0) - new Date(b.Created || 0));
                    break;
                case 'name-asc':
                    sorted.sort((a, b) => a.Title.localeCompare(b.Title));
                    break;
                case 'name-desc':
                    sorted.sort((a, b) => b.Title.localeCompare(a.Title));
                    break;
            }
            
            return sorted;
        },
        paginatedRecipes() {
            return this.filteredRecipes.slice(0, this.recipesToShow);
        },
        hasMore() {
            return this.filteredRecipes.length > this.recipesToShow;
        },
        remainingCount() {
            return this.filteredRecipes.length - this.recipesToShow;
        }
    },
    methods: {
        createNew() {
            this.selectedRecipe = null;
            this.showEditor = true;
        },
        editRecipe(recipe) {
            this.selectedRecipe = recipe;
            this.showEditor = true;
        },
        deleteRecipe(recipe) {
            if(confirm(`Are you sure you want to delete "${recipe.Title}"?`)) {
                this.$store.dispatch('deleteRecipe', recipe.Id);
            }
        },
        handleCloseEditor() {
            // Check if RecipeEditor has unsaved changes
            if (this.$refs.recipeEditor && this.$refs.recipeEditor.hasUnsavedChanges) {
                if (confirm('You have unsaved changes. Are you sure you want to close?')) {
                    this.closeEditor();
                }
            } else {
                this.closeEditor();
            }
        },
        closeEditor() {
            this.showEditor = false;
            this.selectedRecipe = null;
            this.isFullscreen = false;
        },
        handleSaved() {
            this.closeEditor();
        },
        getImageUrl(url) {
            return imageHelper.imageUrl(url);
        },
        loadMore() {
            if (!this.hasMore || this.autoLoading) return;
            this.autoLoading = true;
            this.$nextTick(() => {
                this.recipesToShow += 20;
                requestAnimationFrame(() => { this.autoLoading = false; });
            });
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        handleEditorChangeStatus(hasChanges) {
            this.hasEditorChanges = hasChanges;
        },
        handleCanSaveStatus(canSave) {
            this.canSaveRecipe = canSave;
        }
    }
}
</script>

<style lang="scss" scoped>
.admin-page {
  min-height: 100vh;
  background: var(--lgray);
}

.admin-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  // margin-top is 0 so the ResultsBar above owns the gap; margin-bottom
  // keeps the controls separated from the recipe grid.
  margin: 0 0 30px;
  gap: 20px;
  flex-wrap: wrap;

  .controls-right {
    display: flex;
    gap: 15px;
    align-items: center;
    flex: 1;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .view-toggle {
    display: flex;
    gap: 5px;
    background: white;
    border-radius: 6px;
    padding: 4px;
    box-shadow: var(--cardshadow);
  }

  .view-btn {
    background: transparent;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    color: var(--dark);
    transition: all 0.2s;

    &:hover {
      background: var(--lgray);
    }

    &.active {
      background: var(--darker);
      color: white;
    }
  }

  .sort-select {
    padding: 10px 15px;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    font-family: 'Poppins';
    font-size: 0.9rem;
    background: white;
    color: var(--dark);
    cursor: pointer;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--main);
    }
  }

  .search-field-container {
    max-width: 300px;
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    background: white;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    padding: 0 12px 0 40px;
    transition: all 0.2s;
    
    &:focus-within {
        border-color: var(--main);
    }

    svg {
        color: #999;
        margin-right: 10px;
    }

    input {
        border: none;
        background: transparent;
        font-family: 'Poppins';
        font-size: 0.95rem;
        color: var(--darker);
        width: 100%;
        padding: 10px 0;
        
        &:focus {
            outline: none;
        }

        &::placeholder {
            color: #999;
        }
    }
  }
}

.recipe-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 15px;
  margin-bottom: 40px;

  &.list-view {
    grid-template-columns: 1fr;
    grid-gap: 10px;
  }
}

.admin-recipe-card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  background: white;
  border-radius: 10px;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    .overlay {
      opacity: 1;
    }
  }

  &.list-card {
    display: flex;
    flex-direction: row;
    transform: none;

    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .image {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
      border-radius: 8px 0 0 8px;

      .overlay {
        border-radius: 8px 0 0 8px;

        .edit-icon {
          font-size: 1.2rem;
        }
      }
    }

    .card-content {
      flex: 1;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 15px;
      align-items: center;
      padding: 10px 15px 0 15px;
    }

    .card-info {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;
    }

    h6 {
      font-size: 0.95rem;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .recipe-meta {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin: 0;

      .url-badge, .date-badge {
        font-size: 0.65rem;
        padding: 2px 6px;
      }
    }

    .card-actions {
      display: flex;
      gap: 6px;
      flex-shrink: 0;

      button {
        width: 32px;
        height: 32px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;

        svg {
          font-size: 0.85rem;
        }
      }
    }
  }

  .image {
    position: relative;
    height: 200px;
    background-size: cover;
    background-position: center;
    border-radius: 10px 10px 0 0;

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
      border-radius: 10px 10px 0 0;

      .edit-icon {
        color: white;
        font-size: 2rem;
      }
    }
  }

  .card-content {
    padding: 15px;
  }

  // Card view specific styles
  &:not(.list-card) .card-content {
    h6 {
      text-align: left;
      padding: 0;
      margin-bottom: 8px;
      color: var(--dark);
      // min-height: 2.8em;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // List view keeps its specific h6 styles from the .list-card section above

  .card-content {
    .recipe-meta {
      margin-bottom: 12px;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;

      .url-badge, .date-badge{
        display: inline-block;
        background: var(--lgray);
        color: #666;
        padding: 4px 10px;
        min-width: 30px;
        text-align: center;
        border-radius: 3px;
        font-size: 0.75rem;
      }

      .url-badge {
        font-family: monospace;
      }

      .date-badge {
        color: #666;
      }

      .time-badge {
        background: #d3d3d3;
        color: #4d4d4d;
        padding: 4px 10px;
        width: 30px;
        text-align: center;
        font-size: 12px;
        border-radius: 3px;
      }

      .source-badge {
        background: #c9d6ff;
        color: #2e407d;
        padding: 4px 10px;
        width: 30px;
        text-align: center;
        font-size: 12px;
        border-radius: 3px;
      }

      .description-badge {
        background: #f5d9a5;
        color: #b38600;
        padding: 4px 10px;
        width: 30px;
        text-align: center;
        font-size: 12px;
        border-radius: 3px;
      }

      .done-badge {
        background: #d5ffd9;
        color: #2e7d32;
        padding: 4px 10px;
        width: 30px;
        text-align: center;
        font-size: 12px;
        border-radius: 3px;
      }
    }

    .card-actions {
      display: flex;
      gap: 8px;

      button {
        flex: 1;
        padding: 8px 12px;
        border: none;
        border-radius: 4px;
        font-family: 'Poppins';
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        &.edit-btn {
          background: var(--darker);
          color: white;

          &:hover {
            background: var(--dark);
          }
        }

        &.delete-btn {
          background: #e74c3c;
          color: white;

          &:hover {
            background: #c0392b;
          }
        }
      }
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;

  &.fullscreen-mode {
    max-width: 96vw;
    width: 96vw;
    border-radius: 8px;
    
    .recipe-editor {
      padding: 20px 30px;
    }
  }
}

.recipe-editor {
  overflow-y: auto;
  flex: 1;
}

.modal-header {
  position: sticky;
  top: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px;
  background: linear-gradient(135deg, #e8f5e0 0%, #f0f7ed 100%);
  border-bottom: 2px solid #d8e5e0;
  z-index: 100;
  border-radius: 10px 10px 0 0;
}

.modal-action-btn,
.modal-close {
  background: var(--darker);
  color: white;
  border: none;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--dark);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      background: var(--darker);
    }
  }
}

.fullscreen-btn {
  background: #f39c12 !important;
  
  &:hover {
    background: #e67e22 !important;
  }
}

.modal-close {
  background: #e74c3c;

  &:hover {
    background: #c0392b;
    transform: rotate(90deg);
  }
}

// Auto-load sentinel - matches the front-end LoadMore visual language.
.admin-auto-load {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px 36px;
  min-height: 80px;

  .auto-load-text {
    font-size: 0.8rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--dark);
    opacity: 0.7;
  }

  .auto-load-spinner {
    display: inline-flex;
    gap: 6px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--main);
      animation: admin-auto-load-bounce 1.2s infinite ease-in-out both;
    }
    span:nth-child(1) { animation-delay: -0.32s; }
    span:nth-child(2) { animation-delay: -0.16s; }
  }
}

@keyframes admin-auto-load-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40%           { transform: scale(1);   opacity: 1;   }
}
</style>
